/**
 * FauxDS Pagination - Recreated from Figma MCP
 *
 * ⚠️ This is a SIMPLIFIED RECREATION of the Slate Pagination component for prototyping.
 * It is NOT the real @faire/slate Pagination and should NOT be used for:
 * - Engineering handoff
 * - Design specs
 * - Production code
 *
 * For the real Slate Pagination, see: @faire/slate/components/pagination
 *
 * Props interface aligned with Slate Pagination; implementation unchanged (Figma MCP recreation).
 */
import { cn } from "@faire/prototype-playground/lib/utils";
import React, { forwardRef, useMemo } from "react";

/** @internal Used by implementation */
type PaginationPageAmount = "3" | "5" | "7";
/** @internal Used by implementation */
type PaginationSelection = "Start" | "End" | "Middle";

export interface PaginationProps {
  /**
   * Current active page (1-indexed). Required.
   */
  currentPage: number;
  /**
   * Total number of pages. Required.
   */
  totalPages: number;
  /**
   * Number of page buttons to show including ellipsis. Minimum value is 5.
   * @default 5
   */
  pageAmount?: number;
  /**
   * Get the URL for a given page. If provided, page numbers and prev/next render as links.
   */
  getPageUrl?: (page: number) => string;
  /**
   * Callback when page or navigation button is clicked.
   */
  onClick?: (page: number) => void;

  /**
   * **playground only**
   * Optional className for additional styling
   */
  className?: string;
}

// Chevron left icon
const ChevronLeftIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10 4L6 8L10 12"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Chevron right icon
const ChevronRightIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6 4L10 8L6 12"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Generate pagination items based on Figma design specs
 */
function generatePaginationItems(
  currentPage: number,
  totalPages: number,
  pageAmount: PaginationPageAmount,
  selection: PaginationSelection,
  overflow: boolean
): (number | "ellipsis")[] {
  const amount = parseInt(pageAmount, 10);

  // If total pages is less than or equal to pageAmount, show all pages
  if (totalPages <= amount) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const items: (number | "ellipsis")[] = [];

  if (selection === "Start") {
    // Show first pages: 1, 2, 3, ... (or 1, 2, 3, 4, 5, ... or 1, 2, 3, 4, 5, 6, 7, ...)
    for (let i = 1; i <= amount; i++) {
      items.push(i);
    }
    if (overflow && totalPages > amount) {
      items.push("ellipsis");
      items.push(totalPages);
    }
  } else if (selection === "End") {
    // Show last pages: ... 48, 49, 50 (or similar)
    if (overflow) {
      items.push(1);
      items.push("ellipsis");
    }
    for (let i = totalPages - amount + 1; i <= totalPages; i++) {
      items.push(i);
    }
  } else if (selection === "Middle") {
    // Show middle pages: ... 24, 25, 26, ... (or similar)
    const middlePage = currentPage;
    const halfAmount = Math.floor(amount / 2);
    let start = Math.max(2, middlePage - halfAmount);
    let end = Math.min(totalPages - 1, middlePage + halfAmount);

    // Adjust to show exactly `amount` pages
    if (end - start + 1 < amount) {
      if (start === 2) {
        end = Math.min(totalPages - 1, start + amount - 1);
      } else {
        start = Math.max(2, end - amount + 1);
      }
    }

    if (overflow) {
      items.push(1);
      items.push("ellipsis");
    }
    for (let i = start; i <= end; i++) {
      items.push(i);
    }
    if (overflow) {
      items.push("ellipsis");
      items.push(totalPages);
    }
  }

  return items;
}

/**
 * Pagination - FauxDS (recreated from Figma MCP)
 *
 * Pagination makes it easier for users to browse through a large amount of content
 * by breaking it down into smaller, more manageable pieces.
 *
 * Usage tips:
 * - Clearly indicate the current page number to help users know their location
 * - Limit the number of items displayed in a single view to avoid overwhelming the user
 *
 * This implementation was created by extracting design specs directly from Figma.
 */
const Pagination = forwardRef<HTMLDivElement, PaginationProps>(
  (
    {
      currentPage,
      totalPages,
      pageAmount = 5,
      getPageUrl: _getPageUrl,
      onClick,
      className,
    },
    ref
  ) => {
    const selection: PaginationSelection =
      currentPage <= 1 ? "Start" : currentPage >= totalPages ? "End" : "Middle";
    const pageAmountStr: PaginationPageAmount =
      pageAmount <= 3 ? "3" : pageAmount <= 5 ? "5" : "7";
    const overflow = true;
    const activePage = currentPage;
    const onPageChange = onClick;

    const items = useMemo(
      () =>
        generatePaginationItems(
          activePage,
          totalPages,
          pageAmountStr,
          selection,
          overflow
        ),
      [activePage, totalPages, pageAmountStr, selection, overflow]
    );

    const handlePrevious = () => {
      if (activePage > 1) {
        onPageChange?.(activePage - 1);
      }
    };

    const handleNext = () => {
      if (activePage < totalPages) {
        onPageChange?.(activePage + 1);
      }
    };

    const handlePageClick = (page: number) => {
      onPageChange?.(page);
    };

    if (totalPages <= 1) {
      return null;
    }

    const isPreviousDisabled = activePage === 1;
    const isNextDisabled = activePage === totalPages;

    return (
      <nav
        ref={ref}
        role="navigation"
        aria-label="Pagination"
        className={cn(
          "flex items-center gap-[var(--slate-spacing-element-default)]",
          className
        )}
      >
        {/* Previous button */}
        <button
          onClick={handlePrevious}
          disabled={isPreviousDisabled}
          aria-label="Previous page"
          className={cn(
            "flex h-[32px] w-[32px] items-center justify-center rounded-[var(--slate-radius-component-default)] transition-colors duration-150 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-[var(--slate-color-action-border-focus-ring)] focus-visible:ring-offset-2",
            isPreviousDisabled
              ? "cursor-not-allowed text-[var(--slate-color-action-icon-disabled)]"
              : "text-[var(--slate-color-action-icon-default)] hover:bg-[var(--slate-color-action-surface-inverse-hover)]"
          )}
        >
          <ChevronLeftIcon />
        </button>

        {/* Page numbers */}
        <div
          className="flex items-center gap-[var(--slate-spacing-element-default)]"
          role="group"
          aria-label="Page numbers"
        >
          {items.map((item, index) => {
            if (item === "ellipsis") {
              return (
                <span
                  key={`ellipsis-${index}`}
                  className="flex h-[32px] w-[32px] items-center justify-center text-[var(--slate-color-action-text-default)]"
                  aria-hidden="true"
                  style={{
                    fontFamily: "var(--slate-typography-family-sans-serif)",
                    fontSize: "var(--slate-typography-font-size-200)",
                    fontWeight: "var(--slate-typography-weight-regular)",
                    lineHeight: "var(--slate-typography-line-height-200)",
                    letterSpacing: "var(--slate-typography-letter-spacing-200)",
                  }}
                >
                  …
                </span>
              );
            }

            const isActive = item === activePage;

            return (
              <button
                key={item}
                onClick={() => handlePageClick(item)}
                aria-label={
                  isActive ? `Current page, page ${item}` : `Go to page ${item}`
                }
                aria-current={isActive ? "page" : undefined}
                className={cn(
                  "flex h-[32px] w-[32px] items-center justify-center rounded-[var(--slate-radius-component-default)] transition-colors duration-150 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-[var(--slate-color-action-border-focus-ring)] focus-visible:ring-offset-2",
                  isActive
                    ? "bg-[var(--slate-color-action-surface-default)] text-[var(--slate-color-action-text-inverse)]"
                    : "text-[var(--slate-color-action-text-default)] hover:bg-[var(--slate-color-action-surface-inverse-hover)]"
                )}
                style={{
                  fontFamily: "var(--slate-typography-family-sans-serif)",
                  fontSize: "var(--slate-typography-font-size-200)",
                  fontWeight: isActive
                    ? "var(--slate-typography-weight-medium)"
                    : "var(--slate-typography-weight-regular)",
                  lineHeight: "var(--slate-typography-line-height-200)",
                  letterSpacing: "var(--slate-typography-letter-spacing-200)",
                }}
              >
                {item}
              </button>
            );
          })}
        </div>

        {/* Next button */}
        <button
          onClick={handleNext}
          disabled={isNextDisabled}
          aria-label="Next page"
          className={cn(
            "flex h-[32px] w-[32px] items-center justify-center rounded-[var(--slate-radius-component-default)] transition-colors duration-150 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-[var(--slate-color-action-border-focus-ring)] focus-visible:ring-offset-2",
            isNextDisabled
              ? "cursor-not-allowed text-[var(--slate-color-action-icon-disabled)]"
              : "text-[var(--slate-color-action-icon-default)] hover:bg-[var(--slate-color-action-surface-inverse-hover)]"
          )}
        >
          <ChevronRightIcon />
        </button>
      </nav>
    );
  }
);

Pagination.displayName = "Pagination";

export default Pagination;
